/** @jsx jsx */

import { ChangeEvent, Fragment } from 'react';

import { css, jsx } from '@emotion/core';
import {
  Alert,
  Field,
  LabeledRadio,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';

import CtaCreateZap from 'app/developer-v3/components/CtaCreateZap';
import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import Link from 'app/common/components/Link';
import NotFound from 'app/developer-v3/components/NotFound';
import { BASE_DOC_URL_VB, NEW_SERVICE_FIELD } from 'app/developer-v3/constants';
import { getAppVersionUrl } from 'app/developer-v3/utils';
import {
  useCurrentVersionReadOnlyReason,
  useForm,
} from 'app/developer-v3/hooks';

import {
  FIELD_VERTICAL_SPACING,
  MIN_DESCRIPTION_CHARS,
  MIN_DIRECTIONS_CHARS,
} from './constants';

const Styles = {
  booleanInput: css`
    display: flex;
    gap: 28px;
  `,
  ctaWrapper: css`
    margin-bottom: 30px;
  `,
};

type Display = {
  description: string;
  directions?: string;
  hidden: boolean;
  label: string; // This is aliased as "name" in the UI.
};

type Settings = Display & {
  key: string;
  noun: string;
};

type Props = {
  hasPerform: boolean;
  initialSettings: Settings;
  isTriggerFound: boolean;
  isTriggerNew: boolean;
  isSaving: any;
  onSubmit: (settingsNext: Settings) => Promise<void>;
  params: { id: string; triggerKey: string; version: string };
};

function TriggerSettings({
  hasPerform,
  initialSettings,
  isTriggerFound,
  isTriggerNew,
  isSaving,
  onSubmit,
  params,
}: Props) {
  const form = useForm<Settings>({
    enableReinitialize: true,
    initialValues: initialSettings,
    onSubmit,
    validate: valuesNext => {
      const { description, directions, key, label, noun } = valuesNext;
      const errorsNext: Partial<Record<keyof Settings, string>> = {};

      if (!description) {
        errorsNext.description = 'Description is required.';
      }
      if (description.length < MIN_DESCRIPTION_CHARS) {
        errorsNext.description = `Description must be at least ${MIN_DESCRIPTION_CHARS} characters.`;
      }
      if (directions?.length && directions.length < MIN_DIRECTIONS_CHARS) {
        errorsNext.directions = `Directions must be at least ${MIN_DIRECTIONS_CHARS} characters.`;
      }
      if (!key) {
        errorsNext.key = 'Key is required.';
      }
      if (!/^[A-Z][A-Z0-9_]*$/i.test(key)) {
        errorsNext.key =
          'Keys must start with a letter and contain only alphanumeric and underscore characters.';
      }
      if (!label) {
        errorsNext.label = 'Label is required.';
      }
      if (!noun) {
        errorsNext.noun = 'Noun is required.';
      }

      return errorsNext;
    },
  });

  const baseRoute = getAppVersionUrl(params.id, params.version);
  const cancelRoute = `${baseRoute}/triggers`;
  const isFormReadOnly = !!useCurrentVersionReadOnlyReason() || isSaving;

  return !isTriggerNew && !isTriggerFound ? (
    <NotFound
      escapeRoute={cancelRoute}
      noun="trigger"
      unknownKey={params.triggerKey}
    />
  ) : (
    <EditorBodyLayout
      cancelRoute={cancelRoute}
      isDisabled={
        isFormReadOnly || !form.isDirty || !form.isValid || form.isSubmitting
      }
      isSaving={isSaving}
      onSave={form.onSubmit}
      saveText={
        params.triggerKey === NEW_SERVICE_FIELD ? 'Save and continue' : 'Save'
      }
    >
      <aside css={Styles.ctaWrapper}>
        {hasPerform && <CtaCreateZap type="trigger" />}
        <Alert status="info">
          <Link
            href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#5-7-integration-uses-english-language`}
            target="_blank"
          >
            Use English
          </Link>{' '}
          for all user-facing text.{' '}
          <Link
            href={`${BASE_DOC_URL_VB}/publish/integration-build-guidelines#trigger-design-and-copy`}
            target="_blank"
          >
            Learn more
          </Link>{' '}
          about trigger design and copy.
        </Alert>
      </aside>
      <Field
        error={form.isTouched.key ? form.errors.key : ''}
        helpTextLineClamp={2}
        // Key is not editable if it has been created.
        isDisabled={isFormReadOnly || !isTriggerNew}
        isRequired={true}
        label="Key"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Enter a unique identifier for this trigger using snake_case
            (lowercase with underscores). Example: <code>new_ticket</code>.
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="key"
            name="key"
            onBlur={form.onBlur}
            onChange={form.onChange}
            placeholder="new_ticket"
            value={form.values.key}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.label ? form.errors.label : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Label"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Enter a user-friendly name for this trigger to describe the event
            that makes it run. Capitalize each word. Displayed to users in the
            Zapier UI. Example: <code>New Ticket</code>.
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="label"
            name="label"
            onBlur={form.onBlur}
            onChange={form.onChange}
            placeholder="New Ticket"
            value={form.values.label}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.noun ? form.errors.noun : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Noun"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Specify the main resource this trigger operates on. This should
            match the main resource entered in the Name field above. Capitalize
            all words. Displayed to users in the Zapier UI. Example:{' '}
            <code>Ticket</code>.
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="noun"
            name="noun"
            onBlur={form.onBlur}
            onChange={form.onChange}
            placeholder="Ticket"
            value={form.values.noun}
          />
        )}
      />
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.description ? form.errors.description : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Description"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Clearly describe what this trigger does in a complete sentence.
            Displayed to users in the Zapier UI. Example:{' '}
            <code>Triggers when a new support ticket is created.</code>.
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="description"
            name="description"
            onBlur={form.onBlur}
            onChange={form.onChange}
            placeholder="Triggers when a new support ticket is created."
            value={form.values.description}
          />
        )}
      />
      <Spacer height={20} />
      <Field
        error={form.isTouched.directions ? form.errors.directions : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        label="Directions"
        renderHelpText={() => (
          <Text color={isFormReadOnly ? 'GrayWarm5' : undefined} tag="p">
            Provide brief instructions for users on how and where to copy-paste
            static webhook URLs for a REST hook trigger. Displayed to users in
            the Zapier UI.{' '}
            <Text tag="span" type="SmallPrint3">
              Note:
            </Text>{' '}
            used only for static webhooks.{' '}
            <Link
              href="https://docs.zapier.com/platform/publish/integration-build-guidelines#static-webhook"
              target="_blank"
            >
              Learn more
            </Link>
            .
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="directions"
            name="directions"
            onBlur={form.onBlur}
            onChange={form.onChange}
            value={form.values.directions}
          />
        )}
      />
      <Spacer height={20} />
      <Field
        error={form.isTouched.hidden ? form.errors.hidden : ''}
        helpTextLineClamp={2}
        isDisabled={isFormReadOnly}
        isRequired={true}
        label="Visibility in Editor"
        renderInput={inputProps => (
          <Fragment>
            <div css={Styles.booleanInput}>
              <LabeledRadio
                checked={!form.values.hidden}
                disabled={inputProps.isDisabled}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  if (event.target.checked) {
                    if (!form.isTouched.hidden) {
                      form.setFieldTouched('hidden', true);
                    }
                    form.setFieldValue('hidden', false);
                  }
                }}
                tabIndex={0}
              >
                Shown
              </LabeledRadio>
              <LabeledRadio
                checked={form.values.hidden}
                disabled={inputProps.isDisabled}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  if (event.target.checked) {
                    if (!form.isTouched.hidden) {
                      form.setFieldTouched('hidden', true);
                    }
                    form.setFieldValue('hidden', true);
                  }
                }}
                tabIndex={0}
              >
                Hidden
              </LabeledRadio>
            </div>
            <Text>
              Show or hide this trigger in the Editor when your users are
              setting up a Zap with your app.{' '}
              <Link
                href="https://docs.zapier.com/platform/build/polling-trigger"
                target="_blank"
              >
                Learn more about visibility options.
              </Link>
            </Text>
          </Fragment>
        )}
      />
    </EditorBodyLayout>
  );
}

export default TriggerSettings;
